import React from 'react';
import {
    Box,
    Grid,
    IconButton,
    Button,
    Typography
} from '@mui/material';
import {
    Add,
    Remove,
    Map as MapIcon
} from '@mui/icons-material';

import { BoolField, TextField } from '../../CommonComponents/Form';
import { Popup, SectionDivider } from '../../CommonComponents';
import ShapeAreaEditor from '../../CommonComponents/GoogleMap/ShapeAreaEditor';
import type { AreaShape } from '../../CommonComponents/GoogleMap/ShapeAreaEditor';
import type { EditableRouteMapHandle } from '../../CommonComponents/GoogleMap/EditableRouteMap';
import type { TenantRoute, TenantRoutePoint } from '../../../types/TenantRouteType';
import {
    MAX_TENANT_ROUTES,
    HOME_ROUTE_RADIUS_M,
    circleRoutePoints,
    circleToHexagonPoints,
    nextRouteName,
    polygonToCircle,
    routeColor,
    squareRoutePoints
} from '../../../utils/tenantRoutes';

interface CoverageRoutesBlockProps {
    entity: {
        lat?: number | null,
        lng?: number | null,
        routes?: TenantRoute[]
    },
    onChange: (e: { routes: TenantRoute[] }) => void
}

const CoverageRoutesBlock = ({
    entity,
    onChange
}: CoverageRoutesBlockProps) => {
    const routes = entity.routes || [];
    const [selectedId, setSelectedId] = React.useState<number | null>(routes[0]?.id ?? null);
    const [routeToRemove, setRouteToRemove] = React.useState<TenantRoute | null>(null);
    const mapRef = React.useRef<EditableRouteMapHandle>(null);
    // A click on a card can both change the route and select it (which commits the map) in one event
    const routesRef = React.useRef(routes);
    routesRef.current = routes;

    const selectRoute = (id: number) => {
        mapRef.current?.commitSelected();
        setSelectedId(id);
    };

    React.useEffect(() => {
        if (routes.length === 0) {
            setSelectedId(null);
            return;
        }
        if (!routes.some((route) => route.id === selectedId)) {
            setSelectedId(routes[0].id);
        }
    }, [routes, selectedId]);

    const setRoutes = (next: TenantRoute[]) => {
        routesRef.current = next;
        onChange({ routes: next });
    };

    const addRoute = () => {
        if (routes.length >= MAX_TENANT_ROUTES) return;
        const id = Date.now();
        const next = [
            ...routes,
            {
                id,
                name: nextRouteName(routes),
                points: squareRoutePoints(entity.lat, entity.lng),
                shape: null,
                isPublic: true
            }
        ];
        setRoutes(next);
        setSelectedId(id);
    };

    const removeRoute = (id: number) => {
        setRoutes(routes.filter((route) => route.id !== id));
    };

    // Saved routes with watchlists are confirmed first; those watchlists are paused and detached on save
    const requestRemove = (route: TenantRoute) => {
        if (route.watchlistCount) {
            setRouteToRemove(route);
        } else {
            removeRoute(route.id);
        }
    };

    const renameRoute = (id: number, name: string) => {
        setRoutes(routesRef.current.map((route) => (
            route.id === id ? { ...route, name } : route
        )));
    };

    const setRoutePublic = (id: number, isPublic: boolean) => {
        setRoutes(routesRef.current.map((route) => (
            route.id === id ? { ...route, isPublic } : route
        )));
    };

    const updatePoints = (id: number, points: TenantRoutePoint[]) => {
        setRoutes(routesRef.current.map((route) => (
            route.id === id ? { ...route, points } : route
        )));
    };

    const selectedRoute = routes.find((route) => route.id === selectedId);
    const selectedCircle = selectedRoute?.shape ?? null;

    // A circle is saved as its shape; `points` holds the matching polygon for the map
    const changeCircle = (center: TenantRoutePoint, radiusM: number) => {
        if (!selectedRoute) return;
        setRoutes(routes.map((route) => (route.id === selectedRoute.id ? {
            ...route,
            shape: { type: 'circle', center, radius: radiusM },
            points: circleRoutePoints(center.lat, center.lng, radiusM, 32)
        } : route)));
    };

    const changeShape = (shape: AreaShape) => {
        if (!selectedRoute || (shape === 'circle') === !!selectedCircle) return;
        if (shape === 'circle') {
            const { center, radiusM } = polygonToCircle(selectedRoute.points);
            changeCircle(center, radiusM);
        } else if (selectedCircle) {
            const points = circleToHexagonPoints(selectedCircle.center, selectedCircle.radius);
            setRoutes(routes.map((route) => (route.id === selectedRoute.id ? { ...route, shape: null, points } : route)));
        }
    };

    return (
        <Grid item xs={12}>
            <SectionDivider
                label={`Täckning och rutter (${routes.length})`}
                icon={<MapIcon fontSize="small" />}
                marginTop={2}
                marginBottom={0}
            />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={5}>
                    {routes.map((route, index) => {
                        const selected = route.id === selectedId;
                        return (
                            <Box
                                key={route.id}
                                onClick={() => selectRoute(route.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    boxShadow: (theme) => (selected ? `0 0 0 1px ${theme.palette.primary.main}` : 'none'),
                                    bgcolor: selected ? 'action.selected' : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: selected ? 'action.selected' : 'action.hover'
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: routeColor(index),
                                        flexShrink: 0
                                    }}
                                />
                                <Box sx={{ flex: 1 }} onFocus={() => selectRoute(route.id)}>
                                    <TextField
                                        name={`route-name-${route.id}`}
                                        label="Namn"
                                        value={route.name}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            renameRoute(route.id, e.target.value);
                                        }}
                                        required
                                        maxLength={80}
                                    />
                                    <BoolField
                                        name={`route-public-${route.id}`}
                                        label="Visa på profilsidan"
                                        value={route.isPublic ?? true}
                                        onChange={(e: any) => setRoutePublic(route.id, !!e[`route-public-${route.id}`])}
                                    />
                                    {route.watchlistCount !== undefined && (
                                        <Typography variant="caption">
                                            {`${route.watchlistCount} bevakningar`}
                                        </Typography>
                                    )}
                                </Box>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        requestRemove(route);
                                    }}
                                    color="error"
                                    size="small"
                                    aria-label="Ta bort rutt"
                                >
                                    <Remove />
                                </IconButton>
                            </Box>
                        );
                    })}
                    <Button
                        type="button"
                        startIcon={<Add />}
                        onClick={addRoute}
                        disabled={routes.length >= MAX_TENANT_ROUTES}
                        size="small"
                        sx={{ position: 'relative', zIndex: 1 }}
                    >
                        Lägg till rutt
                    </Button>
                </Grid>
                <Grid item xs={12} md={7} sx={{ position: 'relative', isolation: 'isolate' }}>
                    <ShapeAreaEditor
                        ref={mapRef}
                        shape={selectedCircle ? 'circle' : 'polygon'}
                        onShapeChange={changeShape}
                        showToggle={!!selectedRoute}
                        routes={routes}
                        selectedId={selectedId}
                        fitToId={selectedId}
                        onSelect={selectRoute}
                        onPointsChange={updatePoints}
                        circleCenter={selectedCircle?.center ?? null}
                        circleRadiusM={selectedCircle?.radius ?? HOME_ROUTE_RADIUS_M}
                        onCircleChange={changeCircle}
                        otherRoutes={routes
                            .map((route, index) => ({ ...route, color: routeColor(index) }))
                            .filter((route) => route.id !== selectedId)}
                        height="clamp(250px, 45vh, 420px)"
                    />
                </Grid>
            </Grid>
            <Popup
                open={!!routeToRemove}
                title="Ta bort rutt"
                okLabel="Ta bort"
                handleOk={() => {
                    if (routeToRemove) removeRoute(routeToRemove.id);
                    setRouteToRemove(null);
                }}
                handleClose={() => setRouteToRemove(null)}
            >
                <Typography>
                    {`${routeToRemove?.watchlistCount ?? 0} bevakningar på rutten "${routeToRemove?.name || ''}" `
                        + 'pausas och kopplas loss när företaget sparas, och användarna får en notis.'}
                </Typography>
            </Popup>
        </Grid>
    );
};

export default React.memo(CoverageRoutesBlock);
